import { useState, useEffect } from 'react';
import {
  makeStyles,
  tokens,
  Button,
  Caption1,
  Body2,
} from '@fluentui/react-components';
import { PhoneDesktopAddRegular, Dismiss24Regular } from '@fluentui/react-icons';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const useStyles = makeStyles({
  banner: {
    position: 'fixed' as const,
    bottom: '16px',
    right: '16px',
    maxWidth: '360px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    boxShadow: tokens.shadow28,
    padding: '16px',
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    zIndex: 1000,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    '@media (max-width: 480px)': {
      left: '16px',
      right: '16px',
      bottom: '16px',
      maxWidth: 'none',
    },
  },
  icon: {
    fontSize: '32px',
    color: tokens.colorBrandForeground1,
    flexShrink: 0,
  },
  content: {
    flex: 1,
    minWidth: 0,
  },
  actions: {
    display: 'flex',
    gap: '8px',
    marginTop: '8px',
  },
});

const DISMISS_KEY = 'prism_install_dismissed_v1';

export function InstallPrompt() {
  const styles = useStyles();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Don't show if already dismissed
    if (localStorage.getItem(DISMISS_KEY)) return;

    // Don't show if already running as installed PWA
    if (window.matchMedia('(display-mode: standalone)').matches) return;

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setIsVisible(true);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    if (choice.outcome === 'accepted') {
      setIsVisible(false);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, '1');
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div className={styles.banner}>
      <PhoneDesktopAddRegular className={styles.icon} />
      <div className={styles.content}>
        <Body2 style={{ fontWeight: 600 }}>Install Prism</Body2>
        <Caption1>Get quick access from your home screen or desktop.</Caption1>
        <div className={styles.actions}>
          <Button appearance="primary" size="small" onClick={handleInstall}>
            Install
          </Button>
          <Button appearance="subtle" size="small" onClick={handleDismiss}>
            Not now
          </Button>
        </div>
      </div>
      <Button
        appearance="subtle"
        size="small"
        icon={<Dismiss24Regular />}
        onClick={handleDismiss}
      />
    </div>
  );
}
